import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from "@angular/core";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { InfoService } from "@core-services/info.service";
import { UserService } from "@main-services/user.service";
import { ConfirmModalService } from "@core-services/confirm-modal.service";
import { NewProfileFacade } from "@modules/main/components/profile/new-profile/state/new-profile.facade";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { BpModalCanDismiss, ValidationError } from "@models";
import { CustomModal } from "@modules/core/components/base-modals";
import { TWO_FA_ENABLING_STEPS } from "@helpers/constants";
import { ProfileModel } from "@modules/main/components/profile/new-profile/entities/profile-model";
import { Observable, Subscription } from "rxjs";
import { ToastrService } from "ngx-toastr";
import { AntiPhishingCodeModalService } from "../../services/anti-phishing-code-modal.service";
import { AntiPhishingPutPayload, SecurityVerificationType } from "../../interfaces";
import { SegmentService } from "@main-services/segment.service";
import { PromptsFacade } from "@modules/messaging-prompts/domain/+state/prompts.facade";
import { KycService } from "@modules/kyc/services/kyc.service";
import { maskMobilePhoneNumber } from "@helpers/utils";
import { RudderstackService } from "@main-services/rudderstack.service";

@Component({
	templateUrl: "./anti-phishing-modal.component.html",
	styleUrls: ["./anti-phishing-modal.component.scss"],
	standalone: false
})
export class SecurityAntiPhishingModalComponent extends CustomModal implements BpModalCanDismiss, OnInit, OnDestroy {
	private readonly kycService = inject(KycService);
	private readonly apcModalService = inject(AntiPhishingCodeModalService);
	private readonly promptsFacade = inject(PromptsFacade);
	private readonly cdr = inject(ChangeDetectorRef);
	public readonly steps: number = TWO_FA_ENABLING_STEPS;
	public currentStep: number = 1;

	private smsCodeFormGroupOnChange: Subscription;
	public antiPhishingFormGroup: FormGroup;
	public smsCodeFormGroup: FormGroup;

	public profile$ = this.profileFacade.getProfile$();

	public isSendingSmsCode = false;
	public data: {
		currentAntiPhishingCode: string;
		preventDismiss: boolean;
	};

	public readonly has2fa$: Observable<boolean> = this.userService.has2fa$;
	private readonly segmentService = inject(SegmentService);
	private readonly rudderstackService = inject(RudderstackService);

	constructor(
		protected userService: UserService,
		protected toastr: ToastrService,
		protected apicallService: ApicallService,
		protected infoService: InfoService,
		protected profileFacade: NewProfileFacade,
		protected confirmModalService: ConfirmModalService,
		protected formBuilder: FormBuilder
	) {
		super();
	}

	ngOnInit(): void {
		this.antiPhishingFormGroup = this.formBuilder.group({
			antiPhishingCode: [
				"",
				[
					Validators.required,
					Validators.minLength(4),
					Validators.maxLength(20),
					Validators.pattern("^[a-zA-Z]+$")
				]
			]
		});

		this.smsCodeFormGroup = this.formBuilder.group({
			smsCode: ["", [Validators.minLength(4), Validators.maxLength(4), Validators.pattern("^[0-9]+$")]]
		});

		this.smsCodeFormGroupOnChange = this.smsCodeFormGroup.get("smsCode").valueChanges.subscribe((value) => {
			if (!value) {
				return;
			}
			const code = value.toString();
			if (code.length === 4) {
				this.smsCodeFormGroup.get("smsCode").reset();
				this.sendVerificationCode(code, SecurityVerificationType.PIN_CODE);
			}
			if (code.length > 4) {
				this.smsCodeFormGroup.get("smsCode").setValue(code.slice(0, 4));
			}
		});

		let apcScreenViewedTrackingMessage = "APC Set Up Your Code Screen Viewed";
		if (this.data.currentAntiPhishingCode) {
			apcScreenViewedTrackingMessage = "APC Change Your Code Screen Viewed";
		}
		this.segmentService.track({
			name: apcScreenViewedTrackingMessage
		});
		this.rudderstackService.track({
			name: apcScreenViewedTrackingMessage
		});
	}

	on2FaSave(e: string): void {
		this.sendVerificationCode(e, SecurityVerificationType.AUTHENTICATOR_CODE);
	}

	displaySuccessNotification(): void {
		let titleKey = "anti_phishing_screen_notification_create_success_title";
		let descriptionKey = "anti_phishing_screen_notification_create_success_description";
		if (this.data.currentAntiPhishingCode) {
			titleKey = "anti_phishing_screen_notification_update_success_title";
			descriptionKey = "anti_phishing_screen_notification_update_success_description";
		}
		let title = this.translationService.translate(titleKey);
		let description = this.translationService.translate(descriptionKey);
		this.toastr.success(description, title);
	}

	displayErrorNotification(): void {
		let title = this.translationService.translate("anti_phishing_screen_notification_error_title");
		let description = this.translationService.translate("anti_phishing_screen_notification_error_description");
		this.toastr.error(description, title);
	}

	dotify(text: string): string {
		return text.replace(/\*/g, "•");
	}

	sendVerificationCode(verificationCode: string, type: SecurityVerificationType): void {
		this.isSendingSmsCode = true;
		const code = this.antiPhishingFormGroup.get("antiPhishingCode").value;

		const argumentsObj: AntiPhishingPutPayload = {
			code
		};

		switch (type) {
			case SecurityVerificationType.PIN_CODE:
				argumentsObj.sms_pin_code = verificationCode;
				break;
			case SecurityVerificationType.AUTHENTICATOR_CODE:
				argumentsObj.auth_code = verificationCode;
				break;
			default:
				break;
		}

		const options = new ApicallOption(null, argumentsObj);
		options.raw = true;
		this.apicallService.put("communications/antiphishing-codes", options).subscribe({
			next: (data) => {
				this.onChangeSuccess(this.dotify(data.code));
				this.isSendingSmsCode = false;
			},
			error: (error) => {
				if (error instanceof ValidationError) {
					if (error.responseErrors.some((e) => e.code.indexOf("2FA"))) {
						this.infoService.setError(error);
					} else {
						this.displayErrorNotification();
					}
				} else {
					this.displayErrorNotification();
				}
				this.isSendingSmsCode = false;
			}
		});
	}

	public modalCanDismiss(): boolean | Observable<boolean> {
		if (this.antiPhishingFormGroup.dirty) {
			// Not first step
			return this.confirmModalService.showConfirmModal("close_window?", "unsaved_data_will_get_lost");
		}
		return !this.data.preventDismiss;
	}

	public onClickContinue(): void {
		this.currentStep++;

		let codeScreenViewedTrackingMessage = "APC Set Up Your Code Confirm Clicked";
		if (this.data.currentAntiPhishingCode) {
			codeScreenViewedTrackingMessage = "APC Change Your Code Confirm Clicked";
		}
		this.segmentService.track({
			name: codeScreenViewedTrackingMessage
		});
		this.rudderstackService.track({
			name: codeScreenViewedTrackingMessage
		});

		this.has2fa$.subscribe((has2fa) => {
			let verificationTrackingMessage = "APC Change Your Code SMS Verification Viewed";
			if (has2fa) {
				verificationTrackingMessage = "APC Change Your Code 2FA Screen Viewed ";
			}
			this.segmentService.track({
				name: verificationTrackingMessage
			});
			this.rudderstackService.track({
				name: verificationTrackingMessage
			});

			this.cdr.markForCheck();
		});
	}

	public onChangeSuccess(newCode: string): void {
		this.displaySuccessNotification();
		this.data.currentAntiPhishingCode = this.dotify(newCode);
		this.apcModalService.currentAntiPhishingCode = newCode;
		this.antiPhishingFormGroup.reset();
		this.promptsFacade.disableApcPromptStatus();
		this.kycService.checkKycModal();
		super.close();
	}

	public onClickBack(): void {
		this.decreaseStep();
	}

	protected decreaseStep(): void {
		if (this.currentStep > 1) {
			this.currentStep--;
		}
	}

	public getUserPhone(profile: ProfileModel): string {
		const mobile: string = profile.mobile;
		return maskMobilePhoneNumber(mobile);
	}

	public resendSmsCode(): void {
		this.verifyAntiPhishingCode(true);
	}

	// Check if user's password is correct - API:
	// V2 endpoint will Request SMS
	public verifyAntiPhishingCode(isResend: boolean = false): void {
		const options = new ApicallOption(null, {});

		this.apicallService.post("communications/antiphishing-codes", options).subscribe({
			next: () => {
				if (isResend) {
					this.toastr.success(this.translationService.translate("info_text_verify_pin_code_sent"));
				} else {
					this.onClickContinue();
				}
			},
			error: (error) => {
				if (error instanceof ValidationError) {
					if (error.responseErrors.some((e) => e.code.indexOf("2FA"))) {
						this.infoService.setError(error);
					} else {
						this.displayErrorNotification();
					}
				} else {
					this.displayErrorNotification();
				}
			}
		});
	}

	onApcInputFocused(): void {
		const apcInputFocusedEventName = this.data.currentAntiPhishingCode
			? "APC Change Your Code Enter Code Clicked"
			: "APC Set Up Your Code Enter Code Clicked";
		this.segmentService.track({
			name: apcInputFocusedEventName
		});
		this.rudderstackService.track({
			name: apcInputFocusedEventName
		});
	}

	learnMoreLinkClicked(): void {
		const learnMoreClickedEventName = "APC Help Desk Article Link Clicked";
		const screenItHappenedOn = this.data.currentAntiPhishingCode ? "apc_change_your_code" : "apc_set_up_your_code";
		this.segmentService.track({
			name: learnMoreClickedEventName,
			properties: {
				screen_it_happened_on: screenItHappenedOn
			}
		});
		this.rudderstackService.track({
			name: learnMoreClickedEventName,
			properties: {
				screen_it_happened_on: screenItHappenedOn
			}
		});
	}

	ngOnDestroy(): void {
		this.smsCodeFormGroupOnChange.unsubscribe();
	}
}
